import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Cell,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { formatCurrency, compactCurrencyTick } from "../lib/calculations";
import type {
  ProjectVariationStats,
  MonthlyVariationData,
} from "../hooks/useVariationsStats";

interface ProjectComparisonProps {
  data: ProjectVariationStats[];
}

export function ProjectComparisonChart({ data }: ProjectComparisonProps) {
  const navigate = useNavigate();

  const chartData = data
    .sort((a, b) => b.totalVariations - a.totalVariations)
    .slice(0, 10);

  // Handle bar click - navigate to project detail
  const handleBarClick = (entry: ProjectVariationStats) => {
    if (entry.projectId) {
      navigate(`/projects/${entry.projectId}`);
    }
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          onClick={(state) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const payload = (state as any)?.activePayload?.[0]?.payload;
            if (payload) {
              handleBarClick(payload);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="projectName" hide />
          <YAxis tickFormatter={compactCurrencyTick} />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            wrapperStyle={{ zIndex: 100 }}
          />
          <Legend />
          <Bar
            dataKey="approvedVariations"
            name="Approved"
            fill="var(--color-success)"
            stackId="a"
            cursor="pointer"
          />
          <Bar
            dataKey="pendingVariations"
            name="Pending"
            fill="var(--color-warning)"
            stackId="a"
            cursor="pointer"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface VariationsTrendProps {
  data: MonthlyVariationData[];
}

export function VariationsTrendChart({ data }: VariationsTrendProps) {
  // Take last 12 months
  const chartData = data.slice(-12);

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" />
          <YAxis tickFormatter={compactCurrencyTick} />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            wrapperStyle={{ zIndex: 100 }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="submitted"
            name="Submitted"
            stroke="var(--color-primary)"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="approved"
            name="Approved"
            stroke="var(--color-success)"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface VarianceCreditProps {
  variance: number;
  credit: number;
}

export function VarianceCreditChart({ variance, credit }: VarianceCreditProps) {
  const data = [
    { name: "Variance", value: variance, color: "var(--color-error)" },
    { name: "Credit", value: Math.abs(credit), color: "var(--color-success)" },
  ];

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={compactCurrencyTick} />
          <YAxis type="category" dataKey="name" />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            wrapperStyle={{ zIndex: 100 }}
          />
          <Bar dataKey="value" fill="#8884d8">
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TopProjectsProps {
  data: ProjectVariationStats[];
}

export function TopProjectsChart({ data }: TopProjectsProps) {
  const navigate = useNavigate();

  // Sort by PENDING Variations (Bottleck Focus)
  const chartData = data
    .filter((p) => p.pendingVariations > 0)
    .sort((a, b) => b.pendingVariations - a.pendingVariations)
    .slice(0, 5);

  if (chartData.length === 0) {
    return (
      <div
        style={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-muted)",
        }}
      >
        No pending variations. Good job!
      </div>
    );
  }

  // Handle bar click - navigate to project detail
  const handleBarClick = (data: ProjectVariationStats) => {
    if (data.projectId) {
      navigate(`/projects/${data.projectId}`);
    }
  };

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={compactCurrencyTick} />
          <YAxis
            type="category"
            dataKey="projectName"
            width={100}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            wrapperStyle={{ zIndex: 100 }}
          />
          <Bar
            dataKey="pendingVariations"
            name="Pending Approval"
            fill="var(--color-warning)"
            radius={[0, 4, 4, 0]}
            barSize={24}
            cursor="pointer"
            onClick={(_data, index) => handleBarClick(chartData[index])}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface ClaimsTrendProps {
  data: { month: number; monthName: string; amount: number }[];
}

export function ClaimsTrendChart({ data }: ClaimsTrendProps) {
  // The points come from claimsTrendWindow.ts: the last 12 months on All time,
  // otherwise the months in the date filter. Labels already carry the year.

  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="monthName" />
          <YAxis tickFormatter={compactCurrencyTick} />
          <Tooltip
            formatter={(value: number | undefined) =>
              formatCurrency(value ?? 0)
            }
            wrapperStyle={{ zIndex: 100 }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="amount"
            name="Claims Issued"
            stroke="var(--color-primary)"
            strokeWidth={2}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
